'use client';

import { faRightToBracket } from '@fortawesome/pro-regular-svg-icons';
import { AuthTestIds, getTestId } from '@shared/index';
import { FC } from 'react';
import { Button, Icon, IconButton } from '@/components/ui';
import useCoreConfig from '@/hooks/use-core-config';
import useHeaderContent from '@/hooks/use-header-content';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { cn } from '@/utils';
import { getKeycloakLoginOptions } from '@/utils/keycloak-login';
import UserButton from './user-button';
import UserButtonSkeleton from './user-button-skeleton';

type Props = {
  variant: 'primary' | 'secondary';
  className?: string;
};

const UserAuthToolbarSlot: FC<Props> = ({ variant, className }) => {
  const { t } = useTranslation();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  const { keycloak, isInitialized, isAuthenticated } = useKeycloak();
  const coreConfig = useCoreConfig();
  const headerContent = useHeaderContent();
  const resolveThemeColor = useResolveThemeColor();
  const isSecondary = variant === 'secondary';
  const desktopBreakpoint = isSecondary ? 'xl' : 'lg';
  const desktopBp = isSecondary ? '@xl' : '@lg';
  const mobileBtnClass = isSecondary
    ? 'flex h-auto p-1.5 @xl:hidden'
    : 'flex h-auto p-1.5 @lg:hidden';
  const toolbarTextResolved = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');
  const iconColorStyle = { color: toolbarTextResolved };

  const handleLogin = () =>
    keycloak?.login(getKeycloakLoginOptions({}, coreConfig.defaultLanguageCode));

  if (isStudioPreview) {
    return (
      <>
        <Button
          color="primary"
          isDisabled
          className={cn(
            'hidden',
            `${desktopBp}:ml-1`,
            `${desktopBp}:block`,
            desktopBreakpoint === 'xl' ? 'xl:w-[200px]' : 'lg:w-[200px]',
            className,
          )}
        >
          {t?.common.layout.login}
        </Button>
        <IconButton
          aria-label={t?.common.layout.login ?? 'Login'}
          variant="light"
          className={cn(mobileBtnClass, 'overflow-visible')}
          size="lg"
          isDisabled
          style={iconColorStyle}
        >
          <Icon icon={faRightToBracket} size="lg" style={iconColorStyle} />
        </IconButton>
      </>
    );
  }

  if (!isInitialized) {
    return <UserButtonSkeleton desktopBreakpoint={desktopBreakpoint} className={className} />;
  }

  if (isAuthenticated) {
    if (variant === 'secondary') {
      return (
        <div className="[&_.text-gray-500]:text-gray-400 [&_span]:text-white">
          <UserButton desktopBreakpoint="xl" className={className} />
        </div>
      );
    }

    return <UserButton desktopBreakpoint="lg" className={className} />;
  }

  return (
    <>
      <Button
        color="primary"
        data-testid={getTestId(AuthTestIds.LOGIN_BUTTON)}
        onPress={handleLogin}
        className={cn(
          'hidden',
          `${desktopBp}:ml-1`,
          `${desktopBp}:block`,
          desktopBreakpoint === 'xl' ? 'xl:w-[200px]' : 'lg:w-[200px]',
          className,
        )}
      >
        {t?.common.layout.login}
      </Button>
      <IconButton
        aria-label={t?.common.layout.login ?? 'Login'}
        variant="light"
        className={cn(mobileBtnClass, 'overflow-visible', className)}
        size="lg"
        onPress={handleLogin}
        style={iconColorStyle}
      >
        <Icon icon={faRightToBracket} size="lg" style={iconColorStyle} />
      </IconButton>
    </>
  );
};

export default UserAuthToolbarSlot;
